
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../auth';
import { newsAPI, type NewsItem } from '@/api/news';
import { handleApiError, getApiBaseUrl } from '@/lib/api';

export function Dashboard() {
  const { user } = useAuth();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const newsData = await newsAPI.getNews(5);
      setNews(newsData);
    } catch (error) {
      console.error('Ошибка при загрузке новостей:', handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Обновления': 'bg-blue-100 text-blue-800',
      'База данных': 'bg-emerald-100 text-emerald-800',
      'Информация': 'bg-slate-100 text-slate-800',
      'Безопасность': 'bg-red-100 text-red-800',
      'Обслуживание': 'bg-yellow-100 text-yellow-800',
      'Статистика': 'bg-purple-100 text-purple-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (!user) {
    return null;
  }

  return (
    <div className="container mx-auto max-w-7xl">
      <div className="space-y-8">
        {/* Заголовок */}
        <div>
          <h1 className="text-3xl font-bold">Добро пожаловать, {user.username}!</h1>
          <p className="text-muted-foreground mt-2">
            Главная панель управления системой
          </p>
        </div>

        {/* Основные действия */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>Поиск</CardTitle>
              <CardDescription>Поиск по базе данных</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" asChild>
                <Link to="/search">Начать поиск</Link>
              </Button>
            </CardContent>
          </Card>

          {user.role === 'Администратор' && (
            <Card>
              <CardHeader>
                <CardTitle>Администрирование</CardTitle>
                <CardDescription>Панель управления</CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="secondary" className="w-full" asChild>
                  <Link to="/admin">Открыть панель</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Блок новостей */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">Новости и обновления</h2>
          </div>

          {loading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-200 rounded"></div>
                      <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {news.map((item) => (
                <Card key={item.id} className={`transition-all hover:shadow-md ${item.is_featured ? 'border-blue-200 bg-blue-50/30' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg leading-tight">{item.title}</CardTitle>
                      {item.is_featured && (
                        <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-800">
                          Важное
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Badge className={getCategoryColor(item.category)}>
                        {item.category}
                      </Badge>
                      <span>•</span>
                      <span>{formatDate(item.date)}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.summary}
                    </p>
                    <div className="mt-3 text-xs text-muted-foreground">
                      Автор: {item.author}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {news.length === 0 && !loading && (
            <Card>
              <CardContent className="text-center py-8">
                <p className="text-muted-foreground">Новостей пока нет</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
